import type { EvWorkstationProject } from "../types/index.ts";

export type WorkstationLane = "leads" | "in_touch";

function numeric(value: number | string | null | undefined): number {
  if (value == null) return 0;
  const parsed = typeof value === "number" ? value : Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function normalizeAlpha(value: number | string | null | undefined): number {
  return Math.max(0, Math.min(numeric(value), 100)) / 100;
}

function normalizeDelta(value: number | string | null | undefined): number {
  return Math.max(-1, Math.min(numeric(value) / 20, 1));
}

function dateRank(value: string | null | undefined): number {
  if (!value) return Number.POSITIVE_INFINITY;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? Number.POSITIVE_INFINITY : parsed.getTime();
}

function nullableDateTie(
  left: string | null | undefined,
  right: string | null | undefined
): number {
  return dateRank(left) - dateRank(right);
}

export function isInTouch(item: EvWorkstationProject): boolean {
  const workflow = item.workflow;

  return (
    workflow.stage !== "lead" ||
    Boolean(workflow.groupChatUrl) ||
    Boolean(workflow.transcriptUrl) ||
    Boolean(workflow.callSummary) ||
    Boolean(workflow.lastContactAt) ||
    workflow.nextTouchpointType === "call"
  );
}

export function isLead(item: EvWorkstationProject): boolean {
  const workflow = item.workflow;

  return (
    workflow.stage === "lead" &&
    !workflow.groupChatUrl &&
    !workflow.transcriptUrl &&
    !workflow.callSummary &&
    !workflow.lastContactAt &&
    workflow.nextTouchpointType !== "call"
  );
}

export function scoreInTouch(item: EvWorkstationProject): number {
  return (
    (item.flags.projectFollowUpOverdue ? 1000 : 0) +
    (item.flags.projectFollowUpDueToday ? 800 : 0) +
    (item.flags.callToday ? 600 : 0) +
    (item.flags.missingNextAction ? 400 : 0) +
    normalizeAlpha(item.project.current_alpha_score) * 100 +
    normalizeDelta(item.project.delta_alpha_score) * 50
  );
}

export function scoreLead(item: EvWorkstationProject): number {
  return (
    (item.flags.unworkedWarmLead ? 1200 : 0) +
    (item.flags.warmIntroFollowUpOverdue ? 1000 : 0) +
    (item.flags.warmIntroFollowUpDueToday ? 800 : 0) +
    (item.project.has_tge_hints ? 500 : 0) +
    normalizeDelta(item.project.delta_alpha_score) * 250 +
    normalizeAlpha(item.project.momentum_points) * 150 +
    normalizeAlpha(item.project.current_alpha_score) * 100
  );
}

export function compareInTouchPriority(left: EvWorkstationProject, right: EvWorkstationProject) {
  const scoreDelta = scoreInTouch(right) - scoreInTouch(left);
  if (scoreDelta !== 0) return scoreDelta;

  const followUpDelta = nullableDateTie(
    left.workflow.projectFollowUpDate,
    right.workflow.projectFollowUpDate
  );
  if (followUpDelta !== 0) return followUpDelta;

  const lastContactDelta = nullableDateTie(
    left.workflow.lastContactAt,
    right.workflow.lastContactAt
  );
  if (lastContactDelta !== 0) return lastContactDelta;

  return numeric(right.project.current_alpha_score) - numeric(left.project.current_alpha_score);
}

export function compareLeadPriority(left: EvWorkstationProject, right: EvWorkstationProject) {
  const scoreDelta = scoreLead(right) - scoreLead(left);
  if (scoreDelta !== 0) return scoreDelta;

  const warmIntroDelta = nullableDateTie(
    left.workflow.warmIntroFollowUpDate,
    right.workflow.warmIntroFollowUpDate
  );
  if (warmIntroDelta !== 0) return warmIntroDelta;

  return numeric(right.project.current_alpha_score) - numeric(left.project.current_alpha_score);
}

export function queueForLane(
  items: EvWorkstationProject[],
  lane: WorkstationLane
): EvWorkstationProject[] {
  const predicate = lane === "leads" ? isLead : isInTouch;
  const compare = lane === "leads" ? compareLeadPriority : compareInTouchPriority;
  return items.filter(predicate).sort(compare);
}
